import type { CSSProperties, Ref } from 'vue'

import { computed, inject } from 'vue'

interface UseStickyColumnOptions {
  columnIndex: Ref<number | null>
  baseZIndex?: number
}

interface UseStickyColumnReturn {
  isSticky: Ref<boolean>
  isLastStickyColumn: Ref<boolean>
  stickyStyle: Ref<CSSProperties>
}

/**
 * Composable for sticky column behavior in sf-virtual-table.
 * Handles the transform-based sticky positioning for columns that need to
 * remain visible when horizontally scrolling.
 *
 * @param options.columnIndex - Reactive ref containing the column index (null if not applicable)
 * @param options.baseZIndex - Base z-index for sticky columns (default: 10)
 */
export function useStickyColumn(
  options: UseStickyColumnOptions
): UseStickyColumnReturn {
  const { columnIndex, baseZIndex = 10 } = options

  const stickyColumns = inject<Ref<number>>('stickyColumns')
  const scrollLeft = inject<Ref<number>>('scrollLeft')

  const isSticky = computed<boolean>(() => {
    if (columnIndex.value === null || !stickyColumns) return false
    return columnIndex.value < stickyColumns.value
  })

  const isLastStickyColumn = computed<boolean>(() => {
    if (columnIndex.value === null || !stickyColumns) return false
    return isSticky.value && columnIndex.value === stickyColumns.value - 1
  })

  const stickyStyle = computed<CSSProperties>(() => {
    if (!isSticky.value || columnIndex.value === null || !scrollLeft) return {}
    return {
      transform: `translate3d(${scrollLeft.value}px, 0, 0)`,
      zIndex: baseZIndex - columnIndex.value,
    }
  })

  return {
    isSticky,
    isLastStickyColumn,
    stickyStyle,
  }
}
